import { useState } from 'react';
import { api } from '../../api/client.js';
import type { SessionUser } from '../../api/types.js';
import {
  Alert,
  Field,
  Notice,
  PageHeader,
  StatusPill,
  datum,
  fehlertext,
  useLoad,
} from '../shared/ui.js';

interface U {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  _count: { ownedAccounts: number };
}

/** Interne STAR-Benutzer: Betreiber und Vertrieb. Nur Betreiber. */
export function InternalUsersPage({ user }: { user: SessionUser }): React.JSX.Element {
  const { data, error, reload } = useLoad<{ items: U[] }>('/api/admin/users/internal');
  const [neu, setNeu] = useState({ email: '', displayName: '', role: 'sales' });
  const [geheim, setGeheim] = useState<{ email: string; pw: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function anlegen(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    try {
      const r = await api.post<{ initialPassword: string }>(
        '/api/admin/users/internal',
        neu,
      );
      setGeheim({ email: neu.email, pw: r.initialPassword });
      setNeu({ email: '', displayName: '', role: 'sales' });
      reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  async function patch(id: string, body: object): Promise<void> {
    setErr(null);
    try {
      await api.patch(`/api/admin/users/${id}`, body);
      reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  async function reset(u: U): Promise<void> {
    if (!window.confirm(`Passwort fuer ${u.email} zuruecksetzen?`)) return;
    try {
      const r = await api.post<{ initialPassword: string }>(
        `/api/admin/users/${u.id}/reset-password`,
      );
      setGeheim({ email: u.email, pw: r.initialPassword });
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <>
      <PageHeader
        title="Interne Benutzer"
        lead="Betreiber und Vertrieb. Kundenzugaenge werden am jeweiligen Kunden gepflegt."
      />
      {geheim && (
        <Notice>
          <strong>Einmalpasswort fuer {geheim.email}:</strong>{' '}
          <code className="secret">{geheim.pw}</code>
          <p className="note">
            Nur jetzt sichtbar. Wechselpflicht bei der ersten Anmeldung.
          </p>
          <button type="button" className="link" onClick={() => setGeheim(null)}>
            Ausblenden
          </button>
        </Notice>
      )}
      <Alert text={error ?? err} />
      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Rolle</th>
                <th>Status</th>
                <th className="num">Betreute Kunden</th>
                <th>Letzte Anmeldung</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.displayName}
                    {u.id === user.userId && <span className="note"> (Sie)</span>}
                  </td>
                  <td>{u.email}</td>
                  <td>
                    <select
                      value={u.role}
                      disabled={u.id === user.userId}
                      onChange={(e) => void patch(u.id, { role: e.target.value })}
                    >
                      <option value="sales">Vertrieb</option>
                      <option value="operator_admin">Betreiber</option>
                    </select>
                  </td>
                  <td>
                    <StatusPill active={u.isActive} />
                  </td>
                  <td className="num">{u._count.ownedAccounts}</td>
                  <td>
                    {u.lastLoginAt ? (
                      datum(u.lastLoginAt)
                    ) : (
                      <span className="muted">noch nie</span>
                    )}
                  </td>
                  <td className="row">
                    <button type="button" className="link" onClick={() => void reset(u)}>
                      Passwort zuruecksetzen
                    </button>
                    {u.id !== user.userId && (
                      <button
                        type="button"
                        className="link"
                        onClick={() => void patch(u.id, { isActive: !u.isActive })}
                      >
                        {u.isActive ? 'deaktivieren' : 'aktivieren'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <section className="card">
        <h3>Internen Benutzer anlegen</h3>
        <form onSubmit={anlegen} className="grid2">
          <Field label="E-Mail-Adresse">
            <input
              type="email"
              required
              value={neu.email}
              onChange={(e) => setNeu({ ...neu, email: e.target.value })}
            />
          </Field>
          <Field label="Anzeigename">
            <input
              required
              value={neu.displayName}
              onChange={(e) => setNeu({ ...neu, displayName: e.target.value })}
            />
          </Field>
          <Field label="Rolle" hint="Vertrieb: Kunden und Grundrabatt. Betreiber: alles.">
            <select
              value={neu.role}
              onChange={(e) => setNeu({ ...neu, role: e.target.value })}
            >
              <option value="sales">Vertrieb</option>
              <option value="operator_admin">Betreiber</option>
            </select>
          </Field>
          <div className="row" style={{ alignSelf: 'end' }}>
            <button type="submit" className="primary">
              Anlegen
            </button>
          </div>
        </form>
      </section>
    </>
  );
}
